import React, {useEffect, useMemo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {PLAYER_HOST, PLAYER_PRES_ID_QUERY} from '../../../../../../incase-domain/online';
import {SlideElementHotkeys} from '../../../../../../constants/slideElementHotkeys';
import useCreatePresentation from '../../../../../../service/hooks/useCreatePresentation';
import {TEMPLATE} from '../../../../../../service/template/types';
import {useUpdateUser} from '../../../../../pages/auth/queries';
import {exportPresentationAction, setPresentationSettingsModalAction} from '../../../../../../store/actions/presentation';
import {loggedInUserSelector} from '../../../../../../store/selectors/auth.selectors';
import {exportingPresentationsSelector, selectedPresentationMetaSelector} from '../../../../../../store/selectors/presentation.selectors';
import {isSlideModifiedSelector} from '../../../../../../store/selectors/slide.selectors';
import {presUpdateSpinnerSelector} from '../../../../../../store/selectors/spinner.selector';

type MenuBarFileDropdownProps = {
    fileMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    menuBarOpen: boolean;
    onHoverEnter: () => void;
    onMenuClose: () => void;
    onOpenMenuBar: () => void;
    mutateOnModified: (cb?: () => void) => void;
    onOpenExportPdfModal: () => void;
    onOpenCreatePresentationLinksModal: () => void;
    onOpenSendPresentationModal: () => void;
};

function MenuBarFileDropdown({
    fileMenuItemRef,
    menuBarOpen,
    onHoverEnter,
    onMenuClose,
    onOpenMenuBar,
    mutateOnModified,
    onOpenExportPdfModal,
    onOpenCreatePresentationLinksModal,
    onOpenSendPresentationModal,
}: MenuBarFileDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const createPresentation = useCreatePresentation();
    const {mutate: updateUser} = useUpdateUser();

    useEffect(() => {
        if (!createPresentation.isSuccess || !createPresentation.data) return;

        navigate(`/presentations/${createPresentation.data._id}`, {state: {template: TEMPLATE.TITLE_SUBTITLE}});
        createPresentation.reset();
    }, [navigate, createPresentation]);

    const presUpdateSpinner = useSelector(presUpdateSpinnerSelector);
    const isSlideModified = useSelector(isSlideModifiedSelector);
    const user = useSelector(loggedInUserSelector);
    const exportingPresentations = useSelector(exportingPresentationsSelector);
    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);

    const handleCopyLinkToClipboard = useCallback(async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(`${PLAYER_HOST}?${PLAYER_PRES_ID_QUERY}=${selectedPresentationMeta._id}`);
            toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
        } catch (err) {
            toast.info(t('presEditPage.subNavBar.failedToCopy'));
        }
    }, [t, selectedPresentationMeta._id]);

    const fileMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.fileMenu.new'),
                    icon: 'solar:presentation-graph-linear',
                    onClick: () => {
                        mutateOnModified(() => createPresentation.mutate(undefined));
                    },
                },
                {
                    label: t('menuBar.fileMenu.save'),
                    disabled: presUpdateSpinner || !isSlideModified,
                    icon: 'solar:diskette-linear',
                    onClick: () => mutateOnModified(),
                    shortcuts: SlideElementHotkeys.Save.getPlatformKeys(window.navigator),
                },
                {
                    label: t('menuBar.fileMenu.autoSave'),
                    icon: 'solar:diskette-linear',
                    active: !!user?.autoSave,
                    onClick: () => {
                        if (user) {
                            updateUser({user: {...user, autoSave: !user.autoSave}, userConfig: {}});
                        }
                    },
                },
            ],
            [
                {
                    label: t('menuBar.fileMenu.download'),
                    icon: 'solar:download-minimalistic-linear',
                    onClick: () => {
                        dispatch(exportPresentationAction(selectedPresentationMeta._id));
                    },
                    disabled: exportingPresentations.includes(selectedPresentationMeta._id),
                },
                {
                    label: t('menuBar.fileMenu.exportAs'),
                    icon: 'solar:export-linear',
                    items: [
                        [
                            {
                                label: t('menuBar.fileMenu.pdf'),
                                icon: 'solar:file-download-linear',
                                onClick: onOpenExportPdfModal,
                            },
                            {
                                label: t('menuBar.fileMenu.json'),
                                icon: 'solar:code-file-linear',
                                onClick: () => dispatch(exportPresentationAction(selectedPresentationMeta._id)),
                            },
                        ],
                    ],
                },
                {
                    label: t('menuBar.fileMenu.share'),
                    icon: 'solar:share-linear',
                    items: [
                        [
                            {
                                label: t('menuBar.fileMenu.viewerLinks'),
                                icon: 'solar:link-minimalistic-2-linear',
                                onClick: () =>
                                    selectedPresentationMeta.requestParams?.length
                                        ? onOpenCreatePresentationLinksModal()
                                        : handleCopyLinkToClipboard(),
                            },
                            {
                                label: t('menuBar.fileMenu.collaborate'),
                                icon: 'solar:user-plus-rounded-linear',
                                onClick: onOpenSendPresentationModal,
                            },
                        ],
                    ],
                },
            ],
            [
                {
                    label: t('menuBar.fileMenu.gamification'),
                    icon: 'ph:game-controller-bold',
                    onClick: () => dispatch(setPresentationSettingsModalAction(true, 4)),
                },
                {
                    label: t('menuBar.fileMenu.settings'),
                    icon: 'ph:gear-bold',
                    onClick: () => dispatch(setPresentationSettingsModalAction(true)),
                },
            ],
            [
                {
                    label: t('menuBar.fileMenu.close'),
                    icon: 'ph:x',
                    onClick: () =>
                        mutateOnModified(() => {
                            navigate(`/presentations/`);
                        }),
                },
            ],
        ],
        [
            t,
            presUpdateSpinner,
            isSlideModified,
            user,
            exportingPresentations,
            selectedPresentationMeta._id,
            selectedPresentationMeta.requestParams?.length,
            onOpenExportPdfModal,
            onOpenSendPresentationModal,
            mutateOnModified,
            createPresentation,
            updateUser,
            dispatch,
            onOpenCreatePresentationLinksModal,
            handleCopyLinkToClipboard,
            navigate,
        ]
    );

    return (
        <DropdownMenuCompact
            ref={fileMenuItemRef}
            openOnMouseEnter={menuBarOpen}
            onHoverEnter={onHoverEnter}
            onMenuClose={onMenuClose}
            items={fileMenuItems}
        >
            <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button" onClick={onOpenMenuBar}>
                {t('menuBar.file')}
            </Button>
        </DropdownMenuCompact>
    );
}

export default React.memo(MenuBarFileDropdown);
